/**
 * Evidence from the customer's F&O quotations (lane "header-history",
 * docs/oa-quote-data.md section 5 b and c), read only:
 *
 * - `LastQuotationBadge`: "Last quotation SQ0069312, 30 Sep 2026" next to a header value
 *   that came from the customer's last quotation instead of the customer master.
 * - `LineQuoteHistory`: under a line's price, the customer's last quoted price, quantity,
 *   date and won / lost for the same item, and how often it was quoted.
 * - `CustomerHeaderCard`: the header values of the customer's latest quotations on the
 *   case page; the sales pool and the sales taker are marked as evidence only.
 *
 * Nothing here accepts a line or changes a price: it is information for sales.
 */

import { Badge, Card, DescriptionList, Typography } from "@norican/react";

import type { CustomerHeaderDefault, HeaderFieldSource, QuoteHistoryEvidence } from "@/lib/api";
import type { LinePrice } from "@/lib/mock";
import { formatDay, formatMoney, formatQty } from "@/lib/work-labels";

import { wonPriceGapText } from "./line-totals";

/** What a header field is called on the case page. */
const FIELD_LABEL: Record<CustomerHeaderDefault["field"], string> = {
  sales_unit: "Sales unit",
  sales_responsible: "Sales responsible",
  delivery_terms: "Delivery terms",
  delivery_mode: "Mode of delivery",
  price_group: "Price group",
  sales_pool: "Sales pool",
  sales_taker: "Sales taker",
};

/** True for a well-formed source from the wire (a quotation id and a date). */
export function isFieldSource(value: unknown): value is HeaderFieldSource {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const { ref, as_of: asOf } = value as Record<string, unknown>;
  return typeof ref === "string" && ref !== "" && typeof asOf === "string";
}

/** "Last quotation SQ0069312, 30 Sep 2026". */
export function lastQuotationText(source: HeaderFieldSource): string {
  const day = formatDay(source.as_of);
  return day ? `Last quotation ${source.ref}, ${day}` : `Last quotation ${source.ref}`;
}

/** The badge next to a header value that came from the customer's last quotation. */
export function LastQuotationBadge({ source }: { source: HeaderFieldSource }) {
  return (
    <Badge
      tone="info"
      variant="outline"
      size="xs"
      className="h-auto text-left whitespace-normal"
      data-source={source.ref}
    >
      {lastQuotationText(source)}
    </Badge>
  );
}

/** "Quoted 3 times in the last 18 months, 2 won, 1 lost"; null for no count. */
export function quoteCountText(history: QuoteHistoryEvidence): string | null {
  const quoted = history.n_quoted ?? 0;
  if (quoted <= 0) {
    return null;
  }
  const parts = [`Quoted ${quoted} ${quoted === 1 ? "time" : "times"} in the last 18 months`];
  if ((history.n_won ?? 0) > 0) {
    parts.push(`${history.n_won} won`);
  }
  if ((history.n_lost ?? 0) > 0) {
    parts.push(`${history.n_lost} lost`);
  }
  return parts.join(", ");
}

/** "Last quoted SQ0069312 on 30 Sep 2026: 4 pcs at 100.00 GBP (per 1)". */
export function lastQuoteSentence(history: QuoteHistoryEvidence): string {
  const day = formatDay(history.last_quoted_at);
  const head = `Last quoted ${history.last_quotation_id}${day ? ` on ${day}` : ""}`;
  const qty = history.qty != null ? formatQty(Number(history.qty)) : "";
  const amount = formatMoney(history.sales_price, history.currency);
  const per = Number(history.price_unit ?? 1);
  const perText = Number.isFinite(per) && per > 1 ? ` per ${formatQty(per)}` : "";
  const what = [
    qty && `${qty}${history.unit ? ` ${history.unit}` : ""}`,
    amount && `at ${amount}${perText}`,
  ]
    .filter(Boolean)
    .join(" ");
  return what ? `${head}: ${what}` : head;
}

const WON_LOST_TONE: Record<string, "success" | "danger"> = { won: "success", lost: "danger" };

/** The customer's last quotation of the line's item, under the line's price. */
export function LineQuoteHistory({
  history,
  price = null,
}: {
  history: QuoteHistoryEvidence | null | undefined;
  /** Today's list price of the line: a won price far from it is flagged (P39). */
  price?: LinePrice | null;
}) {
  if (!history) {
    return null;
  }
  const gap = wonPriceGapText(history, price);
  const outcome = (history.won_lost ?? "").trim();
  const tone = WON_LOST_TONE[outcome.toLowerCase()];
  const counts = quoteCountText(history);
  return (
    <span
      className="mt-1 flex flex-col gap-0.5 text-xs"
      data-quote-history={history.last_quotation_id}
    >
      <span className="text-fg-muted break-words">{lastQuoteSentence(history)}</span>
      {gap && (
        <Badge tone="warning" variant="outline" size="xs" className="w-fit" data-price-gap>
          {gap}
        </Badge>
      )}
      {(tone || counts) && (
        <span className="flex flex-wrap items-center gap-1">
          {tone && (
            <Badge tone={tone} variant="subtle" size="xs">
              {outcome}
            </Badge>
          )}
          {counts && <span className="text-fg-subtle break-words">{counts}</span>}
        </span>
      )}
    </span>
  );
}

/** The header values of the customer's latest F&O quotations, on the case page. */
export function CustomerHeaderCard({
  rows,
  className,
}: {
  rows: readonly CustomerHeaderDefault[] | null | undefined;
  className?: string;
}) {
  if (!rows || rows.length === 0) {
    return null;
  }
  return (
    <Card variant="outline" padding="none" className={className}>
      <Card.Header className="border-border-subtle flex items-center gap-2 border-b" padding="sm">
        <Typography variant="label">From the customer&apos;s last quotations</Typography>
      </Card.Header>
      <Card.Body className="flex flex-col gap-2">
        <DescriptionList
          orientation="horizontal"
          aria-label="Header values from the customer's last quotations"
          items={rows.map((row) => ({
            label: FIELD_LABEL[row.field] ?? row.field,
            value: (
              <span className="flex min-w-0 flex-col gap-0.5" data-field={row.field}>
                <span className="text-fg break-words">
                  {row.name ? `${row.name} (${row.value})` : row.value}
                </span>
                <span className="flex flex-wrap items-center gap-1">
                  <LastQuotationBadge source={row.source} />
                  {row.use === "evidence" && (
                    <Badge tone="neutral" variant="outline" size="xs">
                      Not used as a default
                    </Badge>
                  )}
                </span>
              </span>
            ),
          }))}
        />
        <Typography variant="caption" tone="muted" className="break-words">
          Taken from F&amp;O where the customer account has no value. Check them before the
          quotation goes out.
        </Typography>
      </Card.Body>
    </Card>
  );
}
